/**
 * Per-id subscription registry. Replaces the `let _unsubscribe` singletons in
 * `answers/listen-change.ts`, `results/listen-change.ts`, etc., which were
 * clobbered every time the user navigated to a new event.
 *
 * Each `subscribe(id, factory)` call registers a teardown function for that
 * id. Re-subscribing with the same id replaces the old teardown without
 * affecting any other ids. `unsubscribe(id)` and `unsubscribeAll()` are
 * available for cleanup (e.g. on logout).
 */
export type Unsubscribe = () => void;

export type SubscriptionRegistry = {
  /**
   * Register a subscription for `id`. If one already exists, the old teardown
   * runs first. `factory` is invoked synchronously and must return its own
   * unsubscribe function.
   */
  subscribe: (id: string, factory: () => Unsubscribe) => void;
  /** Tear down the subscription for `id`, if any. */
  unsubscribe: (id: string) => void;
  /** Tear down every active subscription. */
  unsubscribeAll: () => void;
  /** Whether `id` currently has an active subscription. */
  isSubscribed: (id: string) => boolean;
  /** Number of active subscriptions. Exposed for tests / diagnostics. */
  size: () => number;
};

export const createSubscriptionRegistry = (): SubscriptionRegistry => {
  const teardowns = new Map<string, Unsubscribe>();

  const unsubscribe = (id: string) => {
    const existing = teardowns.get(id);
    if (existing) {
      try {
        existing();
      } finally {
        teardowns.delete(id);
      }
    }
  };

  return {
    subscribe(id, factory) {
      // Replace, don't stack — if we leak, it's loud (size grows unboundedly).
      unsubscribe(id);
      teardowns.set(id, factory());
    },
    unsubscribe,
    unsubscribeAll() {
      const ids = Array.from(teardowns.keys());
      ids.forEach(unsubscribe);
    },
    isSubscribed(id) {
      return teardowns.has(id);
    },
    size() {
      return teardowns.size;
    },
  };
};
